import type { ReactNode } from 'react';
import {
  BadgeCheck,
  BarChart2,
  Bell,
  Heart,
  Home,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Repeat2,
  Search,
  Share,
} from 'lucide-react';

import { BrandAvatar } from './BrandAvatar';
import { ExpandableCaption } from './ExpandableCaption';
import { PhoneFrame, StatusBar } from './PhoneFrame';
import { PreviewMedia } from './PreviewMedia';
import { useTranslation } from 'react-i18next';

interface XPreviewProps {
  caption: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  mediaSlot?: ReactNode;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
}

const XLogo = ({ size = 18, color = '#fff' }: { size?: number; color?: string }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color} aria-label="X logo">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ActionStat = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <div className="flex items-center gap-1 text-white/65">
    {icon}
    <span className="text-xs text-white/65">{label}</span>
  </div>
);

export const XPreview = ({
  caption,
  mediaUrl,
  mediaType,
  mediaSlot,
  displayName,
  handle,
  avatarUrl,
}: XPreviewProps) => {
  const { t } = useTranslation('contentTest');
  return (
    <PhoneFrame bgcolor="#000" scrollable>
      <StatusBar tone="light" />

      <div className="relative flex items-center justify-center py-2 border-b border-white/10">
        <div className="absolute left-3">
          <BrandAvatar avatarUrl={avatarUrl} size={24} alt={displayName} />
        </div>
        <XLogo size={20} color="#fff" />
      </div>

      <div className="flex gap-2 px-3 pt-2.5">
        <BrandAvatar avatarUrl={avatarUrl} size={40} alt={displayName} />
        <div className="flex-1">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-sm font-bold text-white">{displayName}</span>
            <BadgeCheck size={14} color="#1d9bf0" fill="#1d9bf0" stroke="#fff" />
            <span className="text-[13px] text-white/55">@{handle}</span>
            <span className="text-[13px] text-white/55">{t('contentTest.xpreview.n2h')}</span>
          </div>
          <ExpandableCaption
            data-testid="phone-preview-caption"
            className="text-sm text-white mt-1 leading-[1.4]"
            toggleClassName="mt-0.5 block text-sm font-semibold text-white/70"
          >
            {caption}
          </ExpandableCaption>

          {mediaSlot ??
            (mediaUrl ? (
              <PreviewMedia
                mediaUrl={mediaUrl}
                mediaType={mediaType}
                className="block w-full mt-2 rounded-lg bg-white"
              />
            ) : null)}

          <div className="flex items-center justify-between mt-3 pr-2">
            <ActionStat icon={<MessageCircle size={16} />} label="24" />
            <ActionStat icon={<Repeat2 size={16} />} label="12" />
            <ActionStat icon={<Heart size={16} />} label="148" />
            <ActionStat icon={<BarChart2 size={16} />} label={t('contentTest.xpreview.n24K')} />
            <ActionStat icon={<Share size={16} />} label="" />
          </div>
        </div>
        <MoreHorizontal size={18} color="rgba(255,255,255,0.55)" />
      </div>

      <div className="min-h-6 flex-1 border-t border-white/10 mt-3" />

      <div className="flex items-center justify-around py-2 border-t border-white/10 text-white/70">
        <Home size={18} />
        <Search size={18} />
        <Bell size={18} />
        <Mail size={18} />
      </div>
    </PhoneFrame>
  );
};

XPreview.displayName = 'XPreview';
